import { useRef, useState } from 'react';
import {
  AlertBanner,
  Button,
  ChoiceChip,
  ChoiceChipGroup,
  Dropdown,
  Input,
  ProcessingBasisNote,
} from '@assist/ui';
import { renderProblem } from '@assist/i18n';
import { ApiError, endpoints } from '../state/api.js';
import { useSession } from '../state/session.js';
import { useResource } from '../lib/useResource.js';
import { Screen } from '../components/Screen.js';

/**
 * Layar LAPOR TEMUAN dari konsol — rute `/findings/new` · M02.
 *
 * ACUAN RANCANGANNYA ADALAH LAYAR PONSEL `11:98`, DAN ITU PERLU DINYATAKAN
 * ----------------------------------------------------------------------
 * Peta §5.10 menempatkan "Report a Finding" di aplikasi LAPANGAN, bukan di konsol, dan
 * peta §5.2 memang tidak memberi konsol satu pun layar lapor. Layar ini karena itu
 * TAMBAHAN terhadap ke-35 layar peta, dan `LAYAR-CONSOLE.md` mencatatnya beserta
 * alasannya — aturan §9.7 berlaku: layar yang tidak ada di peta akan membuat peta
 * berhenti dipercaya.
 *
 * ALASANNYA satu, dan ia bukan kenyamanan: tanpa layar ini, alur 1 §6 TIDAK DAPAT
 * DIJALANI sama sekali di sistem yang digelar hari ini, karena aplikasi lapangan belum
 * dibangun. `POST /v1/findings` ada, bekerja, dan tidak punya satu pun pemanggil di
 * konsol. Sebuah alur yang berhasil lewat `curl` dan tidak dapat dijalani manusia adalah
 * alur yang belum selesai.
 *
 * TIGA HAL YANG DITUNTUT ALUR KERJANYA, DAN YANG LAYAR INI KUMPULKAN
 * ----------------------------------------------------------------
 * Guard `CREATE` di `workflows/finding.v1.json` menuntut area, kategori, dan
 * **sekurang-kurangnya satu foto BEFORE** (`PHOTO_REQUIRED`). Ketiganya dikumpulkan di
 * sini, dan fotonya diunggah lewat jalur tiga-permintaan yang sama dengan
 * `PhotoAttachments` (ADR-8: berkas TIDAK melewati API).
 *
 * Yang TIDAK dilakukan: mengirim formulir lalu menampilkan `PHOTO_REQUIRED` dari server.
 * Tombol kirim mati sampai fotonya benar-benar ter-commit, dan alasannya tertulis di
 * bawah tombolnya. Server tetap yang memutuskan — tetapi menyuruh orang mengisi formulir
 * lengkap untuk ditolak atas syarat yang sudah diketahui sejak awal bukan validasi, ia
 * pemborosan waktu orang lain.
 *
 * ACUAN KONSOLNYA KINI ADA: `169:1116` (EN) / `190:1239` (ID)
 * ---------------------------------------------------------
 * Frame 1440x900 dirancang pada 27 September 2026, dan layar ini dibangun ulang
 * mengikutinya. LIMA hal berubah, dan masing-masing punya alasan rancangan yang tertulis:
 *
 * 1. **URUTANNYA MENGIKUTI `11:98`: foto → kategori → lokasi → deskripsi → poin.**
 *    Sebelumnya layar ini teks-lebih-dulu. Urutan ponsel bukan kebetulan: foto adalah
 *    satu-satunya bukti yang membuat temuan dapat diverifikasi nanti, dan meminta orang
 *    menulis sebelum memotret menghasilkan temuan tanpa foto. Penyelia di meja kerja tidak
 *    butuh urutan yang berbeda — ia butuh ruang yang lebih lapang untuk urutan yang sama.
 *
 * 2. **DUA KOLOM 760 / 352, gap 16.** Medan satu kalimat selebar 1128 px adalah kesalahan
 *    usability tersendiri (jarak mata dari awal ke akhir baris). 760 px menjaga panjang
 *    baris `<textarea>` di +/- 95 karakter. Rel kanan memuat tiga hal yang harus terlihat
 *    SAAT mengisi, bukan sesudah: perkiraan poin, dasar pemrosesan, dan apa yang terjadi
 *    berikutnya.
 *
 * 3. **KATEGORI ADALAH CHIP, bukan `<select>`.** Enam pilihan terlihat sekaligus, sekali
 *    sentuh, dan `Keselamatan` berdiri di antaranya sebagai pilihan biasa — bukan sebagai
 *    peringatan merah yang menakuti orang sebelum ia memilih.
 *
 * 4. **MEDAN KEPARAHAN DIHAPUS.** Instruksi perancang apa adanya: "hapus medan keparahan
 *    dari `ReportFinding.tsx`, jangan pindahkan". Figma tidak punya medan itu; kode yang
 *    menambahkannya. Pelapor sering operator yang melihat sesuatu, dan meminta ia menilai
 *    "seberapa serius" pada detik pertama menghasilkan penilaian yang tidak dapat
 *    dibandingkan antar-orang. `Priority` sudah menjadi kolom di Daftar Temuan — tempatnya
 *    di triase, ditetapkan oleh yang menugaskan. Server memakai bawaannya.
 *
 * 5. **PRATINJAU POIN SEBELUM TOMBOL KIRIM**, dengan rinciannya dan satu kalimat bahwa poin
 *    itu PROVISIONAL. Ini menutup satu pertanyaan keadilan: orang melihat bahwa melampirkan
 *    foto menaikkan poin, SEBELUM ia memutuskan melampirkan foto.
 */

const SHA256_UNAVAILABLE = 'SHA256_UNAVAILABLE';

async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
  if (!globalThis.crypto?.subtle) throw new Error(SHA256_UNAVAILABLE);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', buffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export interface ReportFindingProps {
  onCreated: (code: string) => void;
  /** Breadcrumb `‹ Findings` — Figma `169:1116` menggantikan butir navigasi dengannya. */
  onBack?: () => void;
}

export function ReportFinding({ onCreated, onBack }: ReportFindingProps) {
  const { t, translator } = useSession();

  const bootstrap = useResource(
    async () => {
      const [locations, categories] = await Promise.all([
        endpoints.locations(),
        endpoints.itemCategories(),
      ]);
      return {
        locations: locations.items,
        categories: categories.items,
        processing_basis: categories.processing_basis,
      };
    },
    [],
  );

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [locationId, setLocationId] = useState('');
  const [photoId, setPhotoId] = useState<string | null>(null);
  const [photoName, setPhotoName] = useState<string | null>(null);
  const [phase, setPhase] = useState<'uploading' | 'checking' | 'sending' | null>(null);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [localFailure, setLocalFailure] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  async function unggahFoto(file: File) {
    setProblem(null);
    setLocalFailure(null);
    setPhase('uploading');
    try {
      const intent = await endpoints.uploadIntent({
        purpose: 'ITEM_PHOTO',
        mime_type: file.type as 'image/jpeg',
        byte_size: file.size,
        filename: file.name,
      });
      if (intent.max_byte_size && file.size > intent.max_byte_size) {
        setProblem(
          new ApiError(
            {
              code: 'PHOTO_TOO_LARGE',
              status: 422,
              params: {
                max_byte_size: intent.max_byte_size,
                max_pixel_edge: intent.max_pixel_edge ?? 1600,
              },
            },
            422,
          ),
        );
        return;
      }
      const bytes = await file.arrayBuffer();
      const checksum = await sha256Hex(bytes);
      const put = await fetch(intent.upload_url, {
        method: 'PUT',
        headers: { 'content-type': file.type },
        body: bytes,
      });
      if (!put.ok) {
        setLocalFailure('UPLOAD_FAILED');
        return;
      }
      setPhase('checking');
      await endpoints.commitFile(intent.file_id, {
        byte_size: file.size,
        checksum_sha256: checksum,
      });
      setPhotoId(intent.file_id);
      setPhotoName(file.name);
    } catch (err) {
      if (err instanceof Error && err.message === SHA256_UNAVAILABLE) {
        setLocalFailure(SHA256_UNAVAILABLE);
      } else if (err instanceof ApiError) {
        setProblem(err);
      } else {
        setLocalFailure('UPLOAD_FAILED');
      }
    } finally {
      setPhase(null);
    }
  }

  async function kirim() {
    setProblem(null);
    setPhase('sending');
    try {
      const created = await endpoints.createFinding({
        title: title.trim(),
        description: description.trim() === '' ? null : description.trim(),
        category_code: category,
        location_id: locationId,
        /**
         * `severity` TIDAK dikirim. Ia bukan dihilangkan dari muka dan tetap dikirim
         * diam-diam dengan nilai karangan: kontraknya membuatnya opsional, dan nilai
         * bawaan server adalah keputusan server. Sebuah layar yang mengirim `'MEDIUM'`
         * untuk setiap temuan menghasilkan kolom Prioritas yang seluruhnya berisi angka
         * yang tidak pernah dinilai siapa pun — itu lebih buruk daripada kolom kosong,
         * karena ia terlihat seperti penilaian.
         */
        /**
         * `is_safety` TIDAK dikirim sebagai centang terpisah. Ia turunan kategori
         * `SAFETY`, dan dua tempat yang menyatakan "ini soal keselamatan" akan berbeda —
         * lalu SLA eskalasi 24 jam berlaku atau tidak berlaku tergantung tempat mana
         * yang dibaca. Server menurunkannya dari kategori.
         */
        attachments: photoId ? [{ file_object_id: photoId, role: 'BEFORE' }] : [],
      });
      if (created.code) onCreated(created.code);
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setPhase(null);
    }
  }

  const lengkap =
    title.trim().length >= 3 && category !== '' && locationId !== '' && photoId !== null;

  /**
   * PRATINJAU POIN — dihitung di layar, dan itu perlu dikatakan apa adanya.
   *
   * Angkanya BUKAN dari server: `POST /v1/findings` mengembalikan poin sesudah temuannya
   * ada, dan yang dibutuhkan di sini adalah angka SEBELUM orang memutuskan melampirkan
   * foto. Karena itu ia diberi label `provisional` di layar, dengan rinciannya terlihat,
   * dan kalimat di bawahnya mengatakan bahwa poin baru final setelah ketua area
   * mengesahkan perbaikannya. Sebuah angka perkiraan yang tidak menyebut dirinya
   * perkiraan adalah janji.
   *
   * Pengali fotonya 1,2 dan itulah satu-satunya bagian yang berubah oleh pilihan orang
   * di layar ini — yang lain (kategori, zona) datang dari data. Menunjukkannya menutup
   * satu pertanyaan keadilan: orang melihat bahwa melampirkan foto menaikkan poin,
   * sebelum ia memutuskan melampirkan foto.
   */
  const POIN_DASAR = 15;
  const poin = photoId ? Math.round(POIN_DASAR * 1.2) : POIN_DASAR;

  return (
    <Screen
      title={t('report.title')}
      subtitle={t('report.subtitle')}
      resource={bootstrap}
      moduleCode="M02"
    >
      {(data) => (
        <div className="report">
          {/* Breadcrumb menggantikan butir navigasi: peta §4 menetapkan 13 butir dalam 5
              kelompok, dan layar ini sub-alur Temuan — dijangkau dari tombol `New finding`
              di `8:2`. Menambah butir navigasi akan melawan keputusan peta tanpa alasan
              baru. */}
          {onBack ? (
            <p className="report__crumb">
              <button type="button" className="link" onClick={onBack}>
                {t('report.breadcrumb')}
              </button>
            </p>
          ) : null}
          <p className="report__draft">{t('report.draftNote')}</p>

          {problem ? (
            <AlertBanner
              kind="danger"
              signalWord={t('signal.danger')}
              title={renderProblem(translator, problem.problem)}
            />
          ) : null}
          {localFailure ? (
            <AlertBanner
              kind="warning"
              signalWord={t('signal.warning')}
              title={t('report.uploadFailed')}
            />
          ) : null}

          {/* DUA KOLOM 760 / 352, gap 16 — Figma `169:1116` "Badan". */}
          <div className="report__body">
            <div className="report__main">
              {/* 1 · FOTO. Pertama, dan itu urutan `11:98` yang dipertahankan. */}
              <section className="card report__panel">
                <h3 className="report__panelTitle">{t('report.field.photo')}</h3>
                <p className="report__panelMeta">
                  {t('report.photo.count', { used: photoId ? 1 : 0, max: 4, kb: 300 })}
                </p>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  capture="environment"
                  className="photos__input"
                  aria-label={t('finding.photo.choose')}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) void unggahFoto(file);
                  }}
                />
                <p className="photos__action">
                  <Button
                    variant="secondary"
                    busy={phase === 'uploading' || phase === 'checking'}
                    onClick={() => fileInput.current?.click()}
                  >
                    {phase === 'checking'
                      ? t('finding.photo.checking')
                      : phase === 'uploading'
                        ? t('finding.photo.uploading')
                        : photoId
                          ? t('report.photo.replace')
                          : t('report.photo.add')}
                  </Button>
                  {photoName ? <span className="card__muted"> {photoName}</span> : null}
                </p>
                <p className="report__panelHint">{t('report.field.photo.hint')}</p>
              </section>

              {/* 2 · KATEGORI sebagai CHIP. Enam pilihan terlihat sekaligus, sekali
                  sentuh; `Keselamatan` berdiri di antaranya sebagai pilihan biasa. */}
              <section className="card report__panel">
                <h3 className="report__panelTitle">{t('report.field.category')}</h3>
                <p className="report__panelMeta">{t('report.field.choose')}</p>
                <ChoiceChipGroup label={t('report.field.category')}>
                  {data.categories.map((c) => (
                    <ChoiceChip
                      key={c.code}
                      // Nama kategori datang dari katalog, bukan dari server:
                      // `item_category` menyimpan nama per organisasi, dan kode 5R-nya
                      // tetap sama di semuanya.
                      label={t(`category.${c.code}`)}
                      selected={category === c.code}
                      onSelect={() => setCategory(c.code)}
                    />
                  ))}
                </ChoiceChipGroup>
                <p className="report__panelHint">{t('report.field.category.hint')}</p>
              </section>

              {/* 3 · LOKASI sebagai Dropdown — bukan kartu QR. Konsol tidak punya kamera,
                  dan kartu "terdeteksi dari tag QR" akan BERBOHONG di desktop. Kalimat di
                  bawahnya mengatakan mengapa laporan dari ponsel selalu punya lokasi yang
                  tepat sementara yang ini tidak otomatis. */}
              <section className="card report__panel">
                <h3 className="report__panelTitle">{t('report.field.area')}</h3>
                <Dropdown
                  label={t('report.field.area')}
                  value={locationId}
                  onChange={setLocationId}
                  options={[
                    { value: '', label: t('report.field.choose') },
                    ...data.locations
                      .filter((l) => l.state === 'ACTIVE')
                      .map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` })),
                  ]}
                />
                <p className="report__panelHint">{t('report.field.area.hint')}</p>
              </section>

              {/* 4 · APA YANG TERJADI. Judul temuan dan uraiannya, dengan PENGHITUNG
                  karakter — Figma menampilkan "68 / 300 karakter" apa adanya. */}
              <section className="card report__panel">
                <h3 className="report__panelTitle">{t('report.field.description')}</h3>
                <Input
                  label={t('report.field.title')}
                  value={title}
                  maxLength={200}
                  onChange={(e) => setTitle(e.target.value)}
                  hint={t('report.field.title.hint')}
                />
                <label className="assist-field__label" htmlFor="report-description">
                  {t('report.field.description')}
                </label>
                <textarea
                  id="report-description"
                  className="assist-field__input report__textarea"
                  value={description}
                  maxLength={300}
                  rows={3}
                  onChange={(e) => setDescription(e.target.value)}
                />
                <p className="report__panelHint">{t('report.field.description.hint')}</p>
                <p className="report__counter">
                  {t('report.field.description.counter', {
                    used: description.length,
                    max: 300,
                  })}
                </p>
              </section>
            </div>

            {/* REL KANAN 352 — tiga hal yang harus terlihat SAAT mengisi, bukan sesudah. */}
            <aside className="report__rail">
              <section className="card report__panel report__points">
                <h3 className="report__panelTitle">{t('report.points.title')}</h3>
                <p className="report__pointsValue">{poin}</p>
                <p className="report__pointsPill">
                  {t('report.points.provisional', { points: poin })}
                </p>
                <p className="report__panelMeta">
                  {t('report.points.breakdown', {
                    category: category === '' ? t('report.field.choose') : t(`category.${category}`),
                    zone: 3,
                  })}
                </p>
                <p className="report__panelHint">{t('report.points.note')}</p>
              </section>

              {/*
                Kaki dasar pemrosesan — dari SERVER, tidak pernah dari konstanta di layar
                (TELAAH 6, M-7). `POST /v1/findings` adalah operasi tulis atas data tentang
                orang lain, dan orang yang mengisi formulir ini berhak tahu atas dasar apa.
                Figma menamai panelnya *Why this is allowed*, dan kalimat yang paling sering
                hilang ada di dalamnya: "Kewajiban hukum — bukan persetujuan Anda, jadi
                tidak ada yang dapat ditarik."
              */}
              {data.processing_basis ? (
                <ProcessingBasisNote
                  label={t('privacy.basisNote.label')}
                  sentence={t('privacy.basisNote.sentence', {
                    basis: t(`privacy.basis.${data.processing_basis.legal_basis}`),
                  })}
                  article={data.processing_basis.article}
                  purposes={t('privacy.basisNote.purposes', {
                    purposes: data.processing_basis.purposes
                      .map((purpose) => t(`privacy.purpose.${purpose}`))
                      .join(', '),
                  })}
                  photoGuidance={t('privacy.basisNote.photoGuidance')}
                  moreLabel={t('privacy.basisNote.more')}
                  noticeLink={
                    <a className="link" href="/privacy-notice">
                      {t('privacy.notice.link')}
                    </a>
                  }
                />
              ) : null}

              <section className="card report__panel">
                <h3 className="report__panelTitle">{t('report.next.title')}</h3>
                <ol className="report__steps">
                  <li>{t('report.next.1')}</li>
                  <li>{t('report.next.2')}</li>
                  <li>{t('report.next.3')}</li>
                </ol>
              </section>
            </aside>
          </div>

          <p className="page__actions">
            <Button
              variant="primary"
              busy={phase === 'sending'}
              disabled={!lengkap}
              onClick={() => void kirim()}
            >
              {t('report.submit')}
            </Button>
          </p>
          {!lengkap ? <p className="card__muted">{t('report.submit.blocked')}</p> : null}
        </div>
      )}
    </Screen>
  );
}
